import { useEffect } from 'react';

/**
 * Plays a looping ringback ("ringing") tone while `active` is true, so the
 * caller can hear that the call is dialing and knows the moment it's answered
 * (the tone stops).
 *
 * Web implementation: synthesised with the Web Audio API (US ringback —
 * 440 + 480 Hz, 2s on / 4s off) so no audio asset ships in the browser bundle.
 */
export function useRingback(active: boolean) {
    useEffect(() => {
        if (!active || typeof window === 'undefined') return;

        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return;

        const ctx: AudioContext = new AudioCtx();
        ctx.resume().catch(() => {});

        const gain = ctx.createGain();
        gain.gain.value = 0.0001;
        gain.connect(ctx.destination);

        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        osc1.frequency.value = 440;
        osc2.frequency.value = 480;
        osc1.connect(gain);
        osc2.connect(gain);
        osc1.start();
        osc2.start();

        // Cadence: 2s tone, 4s silence, repeating — driven by the gain envelope.
        let stopped = false;
        const timers: ReturnType<typeof setTimeout>[] = [];
        const cycle = () => {
            if (stopped) return;
            const now = ctx.currentTime;
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(0.0001, now);
            gain.gain.exponentialRampToValueAtTime(0.14, now + 0.05); // fade in
            gain.gain.setValueAtTime(0.14, now + 1.95);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 2);  // fade out
            timers.push(setTimeout(cycle, 6000));
        };
        cycle();

        return () => {
            stopped = true;
            timers.forEach(clearTimeout);
            try {
                osc1.stop();
                osc2.stop();
            } catch {
                // already stopped
            }
            ctx.close().catch(() => {});
        };
    }, [active]);
}
